import type { LangMap } from "./index";

/** Recupero automatico, vista studente nei progressi, report classe, avvisi. */
const it: Record<string, unknown> = {
  title: "Attività di recupero suggerite",
  subtitle: "Dai risultati della classe: argomenti da riprendere e attività pronte da assegnare.",
  none: "Nessun recupero necessario: la classe sta andando bene.",
  assign: "Crea e assegna",
  creating: "Sto preparando l'attività…",
  created: "Attività creata e assegnata alla classe",
  kind: { exercise: "Esercizi mirati", explanation: "Rispiegazione", flashcards: "Flashcard di ripasso" },
  reason: {
    mastery: "Padronanza {{mastery}}% su {{topic}}",
    errors: "{{errors}} errori ricorrenti su {{topic}} (padronanza {{mastery}}%)",
  },
  priority: { high: "Urgente", medium: "Da tenere d'occhio" },
  students: "{{count}} studenti in difficoltà",
  tip: { review: "Rileggi la spiegazione", practice: "Fai esercizi mirati", flashcards: "Ripassa con le flashcard" },
  me: {
    title: "I tuoi progressi in classe",
    accuracy: "Risposte corrette",
    submissions: "Consegne",
    mastery: "Padronanza per argomento",
    errors: "Errori tipici",
    yourAnswer: "La tua risposta",
    correct: "Corretta",
    tips: "Consigli di studio per te",
    scope: { personal: "Punto debole tuo", class: "Difficoltà di tutta la classe" },
    none: "Appena consegni le prime attività qui trovi i tuoi progressi.",
    open: "Apri il ripasso",
  },
  report: {
    title: "Report della classe",
    period: "Periodo",
    days: "Ultimi {{n}} giorni",
    all: "Tutto",
    export: "Esporta PDF",
    generating: "Preparo il report…",
    blocked: "Consenti le finestre pop-up per scaricare il report.",
    labels: {
      students: "Studenti",
      assignments: "Attività",
      submissions: "Consegne",
      answered: "Risposte",
      mastery: "Padronanza per argomento",
      errors: "Errori ricorrenti",
      alerts: "Avvisi di difficoltà",
      lessons: "Mini-lezioni AI salvate",
      empty: "Nessun dato nel periodo",
      expected: "Attesa",
      common: "Risposta più frequente",
    },
  },
  alerts: {
    title: "Avvisi didattici",
    teacher: "Classe in difficoltà",
    student: "Argomento critico",
    openClass: "Apri la classe",
    openTopic: "Ripassa {{topic}}",
    topicLine: "{{topic}} · padronanza {{mastery}}%",
    none: "Nessun avviso: tutto sotto controllo.",
  },
};

const en: Record<string, unknown> = {
  title: "Suggested recovery activities",
  subtitle: "From class results: topics to revisit and ready-to-assign activities.",
  none: "No recovery needed: the class is doing well.",
  assign: "Create and assign",
  creating: "Preparing the activity…",
  created: "Activity created and assigned to the class",
  kind: { exercise: "Targeted practice", explanation: "Re-teaching", flashcards: "Review flashcards" },
  reason: {
    mastery: "{{mastery}}% mastery on {{topic}}",
    errors: "{{errors}} recurring errors on {{topic}} ({{mastery}}% mastery)",
  },
  priority: { high: "Urgent", medium: "Keep an eye on it" },
  students: "{{count}} students struggling",
  tip: { review: "Re-read the explanation", practice: "Do targeted practice", flashcards: "Review with flashcards" },
  me: {
    title: "Your progress in this class",
    accuracy: "Correct answers",
    submissions: "Submissions",
    mastery: "Mastery by topic",
    errors: "Typical mistakes",
    yourAnswer: "Your answer",
    correct: "Correct",
    tips: "Study tips for you",
    scope: { personal: "Your weak spot", class: "Whole-class difficulty" },
    none: "Once you submit your first activities your progress shows up here.",
    open: "Open the review",
  },
  report: {
    title: "Class report",
    period: "Period",
    days: "Last {{n}} days",
    all: "All time",
    export: "Export PDF",
    generating: "Preparing the report…",
    blocked: "Allow pop-ups to download the report.",
    labels: {
      students: "Students",
      assignments: "Activities",
      submissions: "Submissions",
      answered: "Answers",
      mastery: "Mastery by topic",
      errors: "Recurring errors",
      alerts: "Difficulty alerts",
      lessons: "Saved AI mini-lessons",
      empty: "No data in this period",
      expected: "Expected",
      common: "Most common answer",
    },
  },
  alerts: {
    title: "Teaching alerts",
    teacher: "Class struggling",
    student: "Critical topic",
    openClass: "Open the class",
    openTopic: "Review {{topic}}",
    topicLine: "{{topic}} · {{mastery}}% mastery",
    none: "No alerts: everything looks fine.",
  },
};

const es: Record<string, unknown> = {
  title: "Actividades de recuperación sugeridas",
  subtitle: "A partir de los resultados de la clase: temas por retomar y actividades listas para asignar.",
  none: "No hace falta recuperación: la clase va bien.",
  assign: "Crear y asignar",
  creating: "Preparando la actividad…",
  created: "Actividad creada y asignada a la clase",
  kind: { exercise: "Ejercicios específicos", explanation: "Volver a explicar", flashcards: "Tarjetas de repaso" },
  reason: {
    mastery: "Dominio {{mastery}}% en {{topic}}",
    errors: "{{errors}} errores recurrentes en {{topic}} (dominio {{mastery}}%)",
  },
  priority: { high: "Urgente", medium: "Para vigilar" },
  students: "{{count}} alumnos con dificultades",
  tip: { review: "Relee la explicación", practice: "Haz ejercicios específicos", flashcards: "Repasa con las tarjetas" },
  me: {
    title: "Tu progreso en clase",
    accuracy: "Respuestas correctas",
    submissions: "Entregas",
    mastery: "Dominio por tema",
    errors: "Errores típicos",
    yourAnswer: "Tu respuesta",
    correct: "Correcta",
    tips: "Consejos de estudio para ti",
    scope: { personal: "Tu punto débil", class: "Dificultad de toda la clase" },
    none: "En cuanto entregues las primeras actividades verás aquí tu progreso.",
    open: "Abrir el repaso",
  },
  report: {
    title: "Informe de la clase",
    period: "Periodo",
    days: "Últimos {{n}} días",
    all: "Todo",
    export: "Exportar PDF",
    generating: "Preparando el informe…",
    blocked: "Permite las ventanas emergentes para descargar el informe.",
    labels: {
      students: "Alumnos",
      assignments: "Actividades",
      submissions: "Entregas",
      answered: "Respuestas",
      mastery: "Dominio por tema",
      errors: "Errores recurrentes",
      alerts: "Avisos de dificultad",
      lessons: "Mini-lecciones IA guardadas",
      empty: "Sin datos en el periodo",
      expected: "Esperada",
      common: "Respuesta más frecuente",
    },
  },
  alerts: {
    title: "Avisos didácticos",
    teacher: "Clase con dificultades",
    student: "Tema crítico",
    openClass: "Abrir la clase",
    openTopic: "Repasar {{topic}}",
    topicLine: "{{topic}} · dominio {{mastery}}%",
    none: "Sin avisos: todo bajo control.",
  },
};

const fr: Record<string, unknown> = {
  title: "Activités de remédiation suggérées",
  subtitle: "D'après les résultats de la classe : notions à reprendre et activités prêtes à attribuer.",
  none: "Pas de remédiation nécessaire : la classe va bien.",
  assign: "Créer et attribuer",
  creating: "Préparation de l'activité…",
  created: "Activité créée et attribuée à la classe",
  kind: { exercise: "Exercices ciblés", explanation: "Nouvelle explication", flashcards: "Cartes de révision" },
  reason: {
    mastery: "Maîtrise {{mastery}}% sur {{topic}}",
    errors: "{{errors}} erreurs récurrentes sur {{topic}} (maîtrise {{mastery}}%)",
  },
  priority: { high: "Urgent", medium: "À surveiller" },
  students: "{{count}} élèves en difficulté",
  tip: { review: "Relis l'explication", practice: "Fais des exercices ciblés", flashcards: "Révise avec les cartes" },
  me: {
    title: "Tes progrès dans cette classe",
    accuracy: "Bonnes réponses",
    submissions: "Rendus",
    mastery: "Maîtrise par notion",
    errors: "Erreurs typiques",
    yourAnswer: "Ta réponse",
    correct: "Correcte",
    tips: "Conseils de révision pour toi",
    scope: { personal: "Ton point faible", class: "Difficulté de toute la classe" },
    none: "Dès que tu rends tes premières activités, tes progrès s'affichent ici.",
    open: "Ouvrir la révision",
  },
  report: {
    title: "Rapport de la classe",
    period: "Période",
    days: "{{n}} derniers jours",
    all: "Tout",
    export: "Exporter en PDF",
    generating: "Préparation du rapport…",
    blocked: "Autorise les fenêtres pop-up pour télécharger le rapport.",
    labels: {
      students: "Élèves",
      assignments: "Activités",
      submissions: "Rendus",
      answered: "Réponses",
      mastery: "Maîtrise par notion",
      errors: "Erreurs récurrentes",
      alerts: "Alertes de difficulté",
      lessons: "Mini-leçons IA enregistrées",
      empty: "Aucune donnée sur la période",
      expected: "Attendue",
      common: "Réponse la plus fréquente",
    },
  },
  alerts: {
    title: "Alertes pédagogiques",
    teacher: "Classe en difficulté",
    student: "Notion critique",
    openClass: "Ouvrir la classe",
    openTopic: "Réviser {{topic}}",
    topicLine: "{{topic}} · maîtrise {{mastery}}%",
    none: "Aucune alerte : tout va bien.",
  },
};

const de: Record<string, unknown> = {
  title: "Vorgeschlagene Fördermaßnahmen",
  subtitle: "Aus den Klassenergebnissen: Themen zum Wiederholen und fertige Aufgaben zum Zuweisen.",
  none: "Keine Förderung nötig: die Klasse ist auf Kurs.",
  assign: "Erstellen und zuweisen",
  creating: "Aufgabe wird vorbereitet…",
  created: "Aufgabe erstellt und der Klasse zugewiesen",
  kind: { exercise: "Gezielte Übungen", explanation: "Neue Erklärung", flashcards: "Wiederholungskarten" },
  reason: {
    mastery: "{{mastery}}% Beherrschung bei {{topic}}",
    errors: "{{errors}} wiederkehrende Fehler bei {{topic}} ({{mastery}}% Beherrschung)",
  },
  priority: { high: "Dringend", medium: "Im Auge behalten" },
  students: "{{count}} Lernende mit Schwierigkeiten",
  tip: { review: "Lies die Erklärung nochmal", practice: "Mach gezielte Übungen", flashcards: "Wiederhole mit Karteikarten" },
  me: {
    title: "Dein Fortschritt in dieser Klasse",
    accuracy: "Richtige Antworten",
    submissions: "Abgaben",
    mastery: "Beherrschung nach Thema",
    errors: "Typische Fehler",
    yourAnswer: "Deine Antwort",
    correct: "Richtig",
    tips: "Lerntipps für dich",
    scope: { personal: "Deine Schwachstelle", class: "Schwierigkeit der ganzen Klasse" },
    none: "Sobald du die ersten Aufgaben abgibst, siehst du hier deinen Fortschritt.",
    open: "Wiederholung öffnen",
  },
  report: {
    title: "Klassenbericht",
    period: "Zeitraum",
    days: "Letzte {{n}} Tage",
    all: "Gesamt",
    export: "PDF exportieren",
    generating: "Bericht wird erstellt…",
    blocked: "Erlaube Pop-ups, um den Bericht herunterzuladen.",
    labels: {
      students: "Lernende",
      assignments: "Aufgaben",
      submissions: "Abgaben",
      answered: "Antworten",
      mastery: "Beherrschung nach Thema",
      errors: "Wiederkehrende Fehler",
      alerts: "Warnhinweise",
      lessons: "Gespeicherte KI-Mini-Lektionen",
      empty: "Keine Daten im Zeitraum",
      expected: "Erwartet",
      common: "Häufigste Antwort",
    },
  },
  alerts: {
    title: "Unterrichtshinweise",
    teacher: "Klasse hat Schwierigkeiten",
    student: "Kritisches Thema",
    openClass: "Klasse öffnen",
    openTopic: "{{topic}} wiederholen",
    topicLine: "{{topic}} · {{mastery}}% Beherrschung",
    none: "Keine Hinweise: alles im grünen Bereich.",
  },
};

const pt: Record<string, unknown> = {
  title: "Atividades de recuperação sugeridas",
  subtitle: "A partir dos resultados da turma: temas a retomar e atividades prontas a atribuir.",
  none: "Sem recuperação necessária: a turma vai bem.",
  assign: "Criar e atribuir",
  creating: "A preparar a atividade…",
  created: "Atividade criada e atribuída à turma",
  kind: { exercise: "Exercícios dirigidos", explanation: "Nova explicação", flashcards: "Flashcards de revisão" },
  reason: {
    mastery: "Domínio {{mastery}}% em {{topic}}",
    errors: "{{errors}} erros recorrentes em {{topic}} (domínio {{mastery}}%)",
  },
  priority: { high: "Urgente", medium: "A acompanhar" },
  students: "{{count}} alunos com dificuldades",
  tip: { review: "Relê a explicação", practice: "Faz exercícios dirigidos", flashcards: "Revê com os flashcards" },
  me: {
    title: "O teu progresso na turma",
    accuracy: "Respostas corretas",
    submissions: "Entregas",
    mastery: "Domínio por tema",
    errors: "Erros típicos",
    yourAnswer: "A tua resposta",
    correct: "Correta",
    tips: "Dicas de estudo para ti",
    scope: { personal: "O teu ponto fraco", class: "Dificuldade de toda a turma" },
    none: "Assim que entregares as primeiras atividades vês aqui o teu progresso.",
    open: "Abrir a revisão",
  },
  report: {
    title: "Relatório da turma",
    period: "Período",
    days: "Últimos {{n}} dias",
    all: "Tudo",
    export: "Exportar PDF",
    generating: "A preparar o relatório…",
    blocked: "Permite as janelas pop-up para transferir o relatório.",
    labels: {
      students: "Alunos",
      assignments: "Atividades",
      submissions: "Entregas",
      answered: "Respostas",
      mastery: "Domínio por tema",
      errors: "Erros recorrentes",
      alerts: "Alertas de dificuldade",
      lessons: "Mini-lições de IA guardadas",
      empty: "Sem dados no período",
      expected: "Esperada",
      common: "Resposta mais frequente",
    },
  },
  alerts: {
    title: "Alertas pedagógicos",
    teacher: "Turma com dificuldades",
    student: "Tema crítico",
    openClass: "Abrir a turma",
    openTopic: "Rever {{topic}}",
    topicLine: "{{topic}} · domínio {{mastery}}%",
    none: "Sem alertas: está tudo bem.",
  },
};

export const recovery: LangMap = { it, en, es, fr, de, pt };
